/**
 * NewRecordingMenu — the inline "create a new recording" menu that appears
 * at the bottom of every recording picker (IVR node editor, system-prompt
 * override editor, bulletin folder item picker, etc).
 *
 * Four built-in actions: upload a file, record in the browser, create from
 * TTS, call-me-to-record. The last one is owned by the phone-recording
 * parallel branch — this menu handles the 404 path gracefully when that
 * branch hasn't merged yet.
 *
 * Extension point: the `extraActions` prop lets future branches (e.g.
 * append/prepend) plug additional rows in without this component having
 * to hard-code every mode. Action buttons render in the order
 * [built-in, ...extraActions].
 *
 * When any action completes, `onCreated(recording)` fires so the caller
 * can immediately select the new row AND invalidate the parent listing.
 */
import { useState, type ReactNode } from 'react';
import type { RecordingRow } from '@/api/recordings';
import { cn } from '@/lib/cn';
import { Upload, Mic, Sparkles, PhoneCall, Plus } from 'lucide-react';
import { UploadInlineDialog } from './UploadInlineDialog';
import { RecordInBrowserDialog } from './RecordInBrowserDialog';
import { CreateFromTtsDialog } from './CreateFromTtsDialog';
import { CallMeToRecordDialog } from './CallMeToRecordDialog';

export interface NewRecordingMenuExtraAction {
  key: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  onClick: () => void;
}

export interface NewRecordingMenuProps {
  /**
   * The customer this recording will be attached to. Super-admin / reseller
   * owner flows usually pass the customer picked by the outer dialog;
   * office-manager flows can leave this undefined and the backend will
   * derive it from the caller's tenant scope.
   */
  customerId?: string | null;
  /** Optional default folder the created recording lands in. */
  folderId?: string | null;
  /** Called immediately after a recording is successfully created. */
  onCreated: (recording: RecordingRow) => void;
  /**
   * Future branches (e.g. append/prepend) append their actions here so we
   * don't have to widen the built-in action list. See doc comment above.
   */
  extraActions?: NewRecordingMenuExtraAction[];
  /** Hide the call-me-to-record option. Used in test envs. */
  hideCallMe?: boolean;
  /** Compact layout (inside a narrow picker popover). Default: true. */
  compact?: boolean;
  /**
   * When true (default), the Upload action accepts multiple files: each is
   * uploaded sequentially and `onCreated` fires once per file when the batch
   * completes, so the parent can react to every upload (e.g. add each to a
   * series folder). Pass false to restrict the picker to a single file.
   */
  multipleUpload?: boolean;
}

type ActionKey = 'upload' | 'record' | 'tts' | 'call_me';

function ActionButton({
  icon, label, description, onClick, compact,
}: {
  icon: ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
  compact: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 text-left transition-colors',
        'hover:bg-slate-50 focus:bg-slate-50 focus:outline-none',
        compact ? 'px-3 py-2' : 'px-4 py-2.5',
      )}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-500">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block font-medium text-slate-700', compact ? 'text-xs' : 'text-sm')}>
          {label}
        </span>
        {description && !compact && (
          <span className="block text-[11px] text-slate-400 truncate">
            {description}
          </span>
        )}
      </span>
      <Plus className="h-3.5 w-3.5 shrink-0 text-slate-300" />
    </button>
  );
}

export function NewRecordingMenu({
  customerId,
  folderId,
  onCreated,
  extraActions = [],
  hideCallMe = false,
  compact = true,
  multipleUpload = true,
}: NewRecordingMenuProps) {
  const [openAction, setOpenAction] = useState<ActionKey | null>(null);

  function handleCreated(recording: RecordingRow) {
    setOpenAction(null);
    onCreated(recording);
  }

  return (
    <>
      <div
        className={cn(
          'border-t border-slate-100 bg-slate-50/50',
          compact ? 'py-1' : 'py-2',
        )}
      >
        <p
          className={cn(
            'px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400',
            compact ? 'pt-1' : 'pt-2',
          )}
        >
          Create new
        </p>

        <ActionButton
          icon={<Upload className="h-3.5 w-3.5" />}
          label="Upload a file"
          onClick={() => setOpenAction('upload')}
          compact={compact}
        />
        <ActionButton
          icon={<Mic className="h-3.5 w-3.5" />}
          label="Record in browser"
          description="Use your microphone to record right here"
          onClick={() => setOpenAction('record')}
          compact={compact}
        />
        <ActionButton
          icon={<Sparkles className="h-3.5 w-3.5" />}
          label="Create from TTS"
          description="Write a script; we synthesize the audio"
          onClick={() => setOpenAction('tts')}
          compact={compact}
        />
        {!hideCallMe && (
          <ActionButton
            icon={<PhoneCall className="h-3.5 w-3.5" />}
            label="Call me to record"
            description="We ring your phone; you speak; we save it"
            onClick={() => setOpenAction('call_me')}
            compact={compact}
          />
        )}

        {extraActions.map((a) => (
          <ActionButton
            key={a.key}
            icon={a.icon ?? <Plus className="h-3.5 w-3.5" />}
            label={a.label}
            description={a.description}
            onClick={a.onClick}
            compact={compact}
          />
        ))}
      </div>

      {openAction === 'upload' && (
        <UploadInlineDialog
          open
          onOpenChange={(o) => { if (!o) setOpenAction(null); }}
          customerId={customerId}
          folderId={folderId}
          onCreated={handleCreated}
          multiple={multipleUpload}
        />
      )}
      {openAction === 'record' && (
        <RecordInBrowserDialog
          open
          onOpenChange={(o) => { if (!o) setOpenAction(null); }}
          customerId={customerId}
          folderId={folderId}
          onCreated={handleCreated}
        />
      )}
      {openAction === 'tts' && (
        <CreateFromTtsDialog
          open
          onOpenChange={(o) => { if (!o) setOpenAction(null); }}
          customerId={customerId}
          folderId={folderId}
          onCreated={handleCreated}
        />
      )}
      {openAction === 'call_me' && (
        <CallMeToRecordDialog
          open
          onOpenChange={(o) => { if (!o) setOpenAction(null); }}
          customerId={customerId}
          folderId={folderId}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}
